<div class="w-full h-auto flex flex-col bg-body-background relative @container">
  @if (isLoading()) {
    <div class="flex items-center justify-center p-20 w-full">
      <i class="fas fa-circle-notch fa-spin text-5xl text-primary"></i>
    </div>
  } @else if (model(); as m) {
    <rapider-page-header
      [title]="m.title"
      titleIcon="fad fa-database text-2xl"
      [showBackButton]="true"
      backButtonText="Back to Database"
      (backClick)="goBack()"
    >
      <div header-title-tags>
        @if (m.domainName) {
          <nz-tag
            class="!m-0 text-xs bg-section-background border-secondary-border text-primary-text"
            >{{ m.domainName }}</nz-tag
          >
        }
      </div>
      <div header-actions class="flex items-center gap-2 shrink-0">
        <div
          class="flex items-center bg-alt-background border border-secondary-border rounded-xl p-1 shadow-sm"
        >
          <button
            (click)="activeView.set('data')"
            [ngClass]="
              activeView() === 'data'
                ? 'bg-component-inner shadow-sm text-primary font-bold'
                : 'text-secondary-text hover:text-primary-text font-medium'
            "
            class="px-3.5 py-1.5 rounded-lg text-xs @md:text-sm transition-all focus:outline-none flex items-center gap-2 cursor-pointer"
            nz-tooltip
            [nzTooltipTitle]="config.texts.tooltipViewData"
          >
            <i class="fas fa-list"></i>
            <span>{{ config.texts.viewData }}</span>
          </button>
          <button
            (click)="activeView.set('spreadsheet')"
            [ngClass]="
              activeView() === 'spreadsheet'
                ? 'bg-component-inner shadow-sm text-primary font-bold'
                : 'text-secondary-text hover:text-primary-text font-medium'
            "
            class="px-3.5 py-1.5 rounded-lg text-xs @md:text-sm transition-all focus:outline-none flex items-center gap-2 cursor-pointer"
            nz-tooltip
            [nzTooltipTitle]="config.texts.tooltipViewSpreadsheet"
          >
            <i class="fas fa-table"></i>
            <span>{{ config.texts.viewSpreadsheet }}</span>
          </button>
          <button
            (click)="activeView.set('schema')"
            [ngClass]="
              activeView() === 'schema'
                ? 'bg-component-inner shadow-sm text-primary font-bold'
                : 'text-secondary-text hover:text-primary-text font-medium'
            "
            class="px-3.5 py-1.5 rounded-lg text-xs @md:text-sm transition-all focus:outline-none flex items-center gap-2 cursor-pointer"
            nz-tooltip
            [nzTooltipTitle]="config.texts.tooltipViewSchema"
          >
            <i class="fas fa-project-diagram"></i>
            <span>{{ config.texts.viewSchema }}</span>
          </button>
        </div>

        <button
          (click)="refreshData()"
          nz-tooltip
          i18n-nzTooltipTitle="@@projectModelDetail.reloadData"
          nzTooltipTitle="Reload Data"
          class="w-9 h-9 rounded-xl flex items-center justify-center text-secondary-text border border-secondary-border hover:bg-hover-default hover:text-primary transition-colors focus:outline-none ml-1 cursor-pointer"
        >
          <i class="fas fa-sync-alt text-xs"></i>
        </button>
        <button
          (click)="toggleDataFullscreen()"
          nz-tooltip
          [nzTooltipTitle]="
            isDataFullscreen() ? 'Exit Fullscreen' : 'Fullscreen'
          "
          class="w-9 h-9 rounded-xl flex items-center justify-center text-secondary-text border border-secondary-border hover:bg-hover-default hover:text-primary transition-colors focus:outline-none cursor-pointer"
        >
          <i
            class="fas text-xs"
            [ngClass]="
              isDataFullscreen()
                ? 'fa-compress-arrows-alt'
                : 'fa-expand-arrows-alt'
            "
          ></i>
        </button>
      </div>
    </rapider-page-header>

    <!-- Table Context Hero Banner -->
    <div class="px-4 sm:px-6 lg:px-7 pt-1 pb-3 flex flex-col gap-4 w-full">
      <div class="bg-component-inner border border-primary-border rounded-2xl p-4 @md:p-5 shadow-sm flex flex-col @2xl:flex-row @2xl:items-center justify-between gap-4">
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 rounded-xl bg-alt-background border border-secondary-border/70 text-secondary-text dark:text-zinc-300 flex items-center justify-center text-xl shrink-0 shadow-xs">
            <i class="fas fa-table"></i>
          </div>
          <div class="flex flex-col">
            <div class="flex items-center gap-2.5 flex-wrap">
              <h1 class="text-xl @lg:text-2xl font-extrabold text-heading m-0 tracking-tight">{{ m.title }}</h1>
              <span class="text-xs font-mono font-semibold px-2 py-0.5 rounded-md border border-secondary-border bg-alt-bold-background text-secondary-text">
                tbl: {{ m.dataTableName || m.name }}
              </span>
              @if (m.domainName) {
                <span class="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-alt-background text-secondary-text border border-secondary-border/70">
                  <i class="fad fa-database text-[11px]"></i> {{ m.domainName }} Database
                </span>
              }
            </div>
            <p class="text-xs @md:text-sm text-secondary-text mt-1 mb-0 line-clamp-2 max-w-3xl">
              {{ m.description || 'Relational data model table with automated CRUD operations, validations, and AI schema embeddings.' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3 shrink-0 self-end @2xl:self-center">
          <div class="flex items-center gap-3 px-3 py-1.5 rounded-xl bg-alt-background/70 border border-secondary-border/60 text-xs">
            <span class="inline-flex items-center gap-1 text-primary-text font-semibold">
              <i class="fas fa-columns text-primary text-[11px]"></i>
              {{ m.fields?.length || 0 }} Columns
            </span>
            <span class="text-secondary-text/40">•</span>
            <span class="inline-flex items-center gap-1 text-primary-text font-semibold">
              <i class="fas fa-link text-indigo-500 text-[11px]"></i>
              {{ m.relations?.length || 0 }} Relations
            </span>
            <span class="text-secondary-text/40">•</span>
            <span class="inline-flex items-center gap-1 text-primary-text font-semibold">
              <i class="fas fa-network-wired text-sky-500 text-[11px]"></i>
              {{ m.endpoints?.length || 0 }} Endpoints
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      class="px-4 sm:px-6 lg:px-7 pt-0 pb-6 flex flex-col gap-6 w-full"
    >
      @if (activeView() === 'data' || activeView() === 'spreadsheet') {
        <section
          class="bg-component-inner border border-primary-border rounded-xl shadow-sm flex flex-col transition-all duration-300 w-full"
          [ngClass]="
            isDataFullscreen()
              ? 'fixed inset-0 z-[100] m-4 !rounded-xl shadow-2xl border-2'
              : ''
          "
        >
          @if (isDataFullscreen()) {
            <div
              class="flex items-center justify-between px-4 py-2.5 border-b border-secondary-border bg-component-inner/95 backdrop-blur-sm shrink-0 rounded-t-xl z-20"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded-lg bg-alt-background border border-secondary-border flex items-center justify-center text-sm text-secondary-text shadow-xs"
                >
                  <i class="fas fa-table"></i>
                </div>
                <div class="flex items-center gap-2">
                  <span class="font-extrabold text-heading text-sm">{{ m.title }}</span>
                  <span
                    class="text-xs font-mono font-semibold px-2 py-0.5 rounded-md border border-secondary-border bg-alt-bold-background text-secondary-text"
                  >
                    {{ m.dataTableName || m.name }}
                  </span>
                  <span
                    class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20"
                  >
                    Fullscreen
                  </span>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <div
                  class="flex items-center bg-alt-background border border-secondary-border rounded-lg p-0.5 shadow-xs"
                >
                  <button
                    (click)="activeView.set('data')"
                    [ngClass]="
                      activeView() === 'data'
                        ? 'bg-component-inner shadow-xs text-primary font-bold'
                        : 'text-secondary-text hover:text-primary-text font-medium'
                    "
                    class="px-2.5 py-1 rounded-md text-xs transition-all focus:outline-none flex items-center gap-1.5 cursor-pointer"
                    nz-tooltip
                    [nzTooltipTitle]="config.texts.tooltipViewData"
                  >
                    <i class="fas fa-list text-[11px]"></i>
                    <span>Data</span>
                  </button>
                  <button
                    (click)="activeView.set('spreadsheet')"
                    [ngClass]="
                      activeView() === 'spreadsheet'
                        ? 'bg-component-inner shadow-xs text-primary font-bold'
                        : 'text-secondary-text hover:text-primary-text font-medium'
                    "
                    class="px-2.5 py-1 rounded-md text-xs transition-all focus:outline-none flex items-center gap-1.5 cursor-pointer"
                    nz-tooltip
                    [nzTooltipTitle]="config.texts.tooltipViewSpreadsheet"
                  >
                    <i class="fas fa-table text-[11px]"></i>
                    <span>Spreadsheet</span>
                  </button>
                </div>

                <button
                  (click)="refreshData()"
                  nz-tooltip
                  nzTooltipTitle="Reload Data"
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-secondary-text border border-secondary-border hover:bg-hover-default hover:text-primary transition-colors focus:outline-none cursor-pointer"
                >
                  <i class="fas fa-sync-alt text-xs"></i>
                </button>

                <button
                  (click)="toggleDataFullscreen()"
                  nz-button
                  nzType="default"
                  class="flex items-center gap-1.5 !rounded-lg text-xs font-semibold text-secondary-text hover:text-primary border border-secondary-border shadow-xs hover:bg-hover-default transition-all cursor-pointer h-8 px-3"
                  nz-tooltip
                  nzTooltipTitle="Exit Fullscreen (Esc)"
                >
                  <i class="fas fa-compress-arrows-alt text-xs"></i>
                  <span>Exit Fullscreen</span>
                </button>
              </div>
            </div>
          }

          <div
            class="p-2 flex flex-col gap-2 w-full"
            [ngClass]="isDataFullscreen() ? 'flex-1 h-full min-h-0 overflow-hidden' : ''"
          >
            <div
              class="w-full transition-all duration-300"
              [ngClass]="
                isDataFullscreen()
                  ? 'flex-1 h-full min-h-0 flex flex-col overflow-hidden'
                  : 'flex flex-col h-auto'
              "
            >
              @if (activeView() === 'spreadsheet') {
                <rapider-project-model-spreadsheet-viewer
                  class="flex-1 flex flex-col w-full h-full min-h-0"
                  [projectModelName]="m.name"
                  [explicitModel]="m"
                  [refreshTrigger]="dataRefreshTrigger()"
                  [isFullscreen]="isDataFullscreen()"
                  (toggleFullscreen)="toggleDataFullscreen()"
                >
                </rapider-project-model-spreadsheet-viewer>
              } @else if (activeView() === 'data') {
                <rapider-project-model-data-viewer
                  class="flex-1 flex flex-col w-full h-full min-h-0"
                  [projectModelName]="m.name"
                  [explicitModel]="m"
                  [refreshTrigger]="dataRefreshTrigger()"
                  [isFullscreen]="isDataFullscreen()"
                  (toggleFullscreen)="toggleDataFullscreen()"
                >
                </rapider-project-model-data-viewer>
              }
            </div>
          </div>
        </section>
      }

      @if (activeView() === 'schema') {
        <section
          class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden flex flex-col"
        >
          <div class="p-6 flex flex-col gap-4">
            @if (hasSchemaChanges()) {
              <div
                class="bg-warning/10 border border-warning/30 text-warning-text p-4 rounded-xl shadow-sm flex items-start gap-3 animate-fade-in"
              >
                <i class="fas fa-exclamation-triangle mt-1"></i>
                <span class="text-sm font-medium leading-relaxed">{{
                  config.texts.migrationWarning
                }}</span>
              </div>
            }

            <div class="flex justify-between items-center w-full mt-2">
              <h2 class="text-2xl font-bold text-heading m-0">
                <i class="fad fa-list-ul mr-2 text-primary"></i
                >{{ m.title }}&nbsp;{{ config.texts.fieldsTitle }}
              </h2>
              @if (!isFreeTier()) {
                <button
                  nz-button
                  nzType="primary"
                  (click)="openFieldModal()"
                  class="flex items-center gap-2 font-bold rounded-lg shadow-sm border-none bg-primary text-white focus:outline-none"
                >
                  <i class="fas fa-plus"></i>
                  <span i18n="@@database.addFieldBtn">Add Field</span>
                </button>
              } @else {
                <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                  <i class="fas fa-lock text-xs"></i>
                  <span>Managed Schema</span>
                </div>
              }
            </div>
            <nz-table
              #fieldsTable
              [nzData]="m.fields || []"
              [nzLoading]="isSchemaRefreshing()"
              [nzShowPagination]="false"
              nzSize="small"
              class="border border-secondary-border rounded overflow-hidden mt-2"
            >
              <thead>
                <tr class="bg-table-header-background">
                  <th
                    class="!bg-transparent text-primary-text w-10 text-center"
                  ></th>
                  <th class="!bg-transparent text-primary-text">
                    {{ config.texts.tableFieldName }}
                  </th>
                  <th class="!bg-transparent text-primary-text">
                    {{ config.texts.tableFieldType }}
                  </th>
                  <th class="!bg-transparent text-primary-text">
                    {{ config.texts.tableFieldFlags }}
                  </th>
                  <th class="!bg-transparent text-primary-text">
                    {{ config.texts.tableFieldDesc }}
                  </th>
                  <th
                    class="!bg-transparent text-primary-text text-right"
                    i18n="@@database.tableFieldActions"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody cdkDropList (cdkDropListDropped)="onFieldDrop($event)">
                @for (
                  field of m.fields;
                  track field.id || field.name || $index
                ) {
                  <tr
                    class="hover:bg-hover-table bg-component-inner transition-colors"
                    cdkDrag
                    [cdkDragDisabled]="isSystemField(field) || isFreeTier()"
                  >
                    <td
                      class="!bg-transparent border-t border-secondary-border text-center w-10"
                    >
                      @if (!isSystemField(field) && !isFreeTier()) {
                        <div
                          cdkDragHandle
                          class="cursor-grab active:cursor-grabbing inline-block p-1"
                        >
                          <i
                            class="fas fa-grip-vertical text-secondary-text hover:text-primary transition-colors"
                          ></i>
                        </div>
                      }
                    </td>
                    <td
                      class="!bg-transparent border-t border-secondary-border font-mono text-sm font-semibold text-primary-text"
                    >
                      {{ field.name }}
                      @if (field.calculation) {
                        <nz-tag
                          [nzColor]="'purple'"
                          class="!m-0 text-[10px] ml-2 font-sans font-bold"
                          nz-tooltip
                          [nzTooltipTitle]="field.calculation.formula"
                        >
                          <i class="fas fa-calculator mr-1"></i> Calculated
                        </nz-tag>
                      }
                    </td>
                    <td
                      class="!bg-transparent border-t border-secondary-border"
                    >
                      <nz-tag
                        class="!m-0 text-xs bg-section-background border-secondary-border text-primary-text font-bold"
                        >{{ field.type }}{{ field.isArray ? '[]' : '' }}</nz-tag
                      >
                    </td>
                    <td
                      class="!bg-transparent border-t border-secondary-border flex gap-1 flex-wrap items-center h-full min-h-[48px]"
                    >
                      @if (field.isPrimaryKey) {
                        <nz-tag
                          [nzColor]="'gold'"
                          class="!m-0 text-[10px] font-bold"
                          >PK</nz-tag
                        >
                      }
                      @if (field.isRequired) {
                        <nz-tag
                          [nzColor]="'error'"
                          class="!m-0 text-[10px] font-bold"
                          >Required</nz-tag
                        >
                      }
                      @if (field.isUnique) {
                        <nz-tag
                          [nzColor]="'cyan'"
                          class="!m-0 text-[10px] font-bold"
                          >Unique</nz-tag
                        >
                      }
                      @if (field.isHidden) {
                        <nz-tag
                          class="!m-0 text-[10px] text-secondary-text bg-alt-background font-bold"
                          >Hidden</nz-tag
                        >
                      }
                      @if (field.isSensitive) {
                        <nz-tag
                          [nzColor]="'red'"
                          class="!m-0 text-[10px] font-bold"
                          >Sensitive</nz-tag
                        >
                      }
                    </td>
                    <td
                      class="!bg-transparent border-t border-secondary-border text-sm text-secondary-text"
                    >
                      {{ field.description }}
                    </td>
                    <td
                      class="!bg-transparent border-t border-secondary-border text-right whitespace-nowrap"
                    >
                      @if (!isSystemField(field) && !isFreeTier()) {
                        <button
                          nz-button
                          nzType="text"
                          (click)="openFieldModal(field)"
                          nz-tooltip
                          i18n-nzTooltipTitle="@@database.editField"
                          nzTooltipTitle="Edit Field"
                          class="text-secondary-text hover:text-primary focus:outline-none"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          nz-button
                          nzType="text"
                          nzDanger
                          nz-popconfirm
                          [nzPopconfirmTitle]="config.texts.deleteFieldConfirm"
                          (nzOnConfirm)="deleteField(field.id)"
                          nz-tooltip
                          i18n-nzTooltipTitle="@@database.deleteField"
                          nzTooltipTitle="Delete Field"
                          class="text-secondary-text hover:text-danger focus:outline-none"
                        >
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      } @else if (isFreeTier()) {
                        <span
                          nz-tooltip
                          nzTooltipTitle="Managed schema defined by App Store application"
                          class="text-secondary-text/50 text-xs px-2"
                        >
                          <i class="fas fa-shield-alt text-xs"></i>
                        </span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </nz-table>
          </div>
        </section>

        @if (m.relations && m.relations.length > 0) {
          <section
            class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden flex flex-col"
          >
            <div class="p-6 flex flex-col gap-4">
              <h2 class="text-2xl font-bold text-heading">
                <i class="fad fa-project-diagram mr-2 text-primary"></i
                >{{ m.title }}&nbsp;{{ config.texts.relationsTitle }}
              </h2>
              <nz-table
                #relationsTable
                [nzData]="m.relations"
                [nzShowPagination]="false"
                nzSize="small"
                class="border border-secondary-border rounded overflow-hidden mt-2"
              >
                <thead>
                  <tr class="bg-table-header-background">
                    <th class="!bg-transparent text-primary-text">
                      {{ config.texts.tableRelName }}
                    </th>
                    <th class="!bg-transparent text-primary-text">
                      Source Model
                    </th>
                    <th class="!bg-transparent text-primary-text">
                      {{ config.texts.tableRelType }}
                    </th>
                    <th class="!bg-transparent text-primary-text">
                      {{ config.texts.tableRelTarget }}
                    </th>
                    <th class="!bg-transparent text-primary-text">
                      {{ config.texts.tableRelKeys }}
                    </th>
                    <th class="!bg-transparent text-primary-text">
                      {{ config.texts.tableRelDesc }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (
                    rel of m.relations;
                    track rel.id || rel.name || $index
                  ) {
                    <tr class="hover:bg-hover-table">
                      <td
                        class="!bg-transparent border-t border-secondary-border font-mono text-sm font-semibold text-primary-text"
                      >
                        {{ rel.name }}
                      </td>
                      <td
                        class="!bg-transparent border-t border-secondary-border text-sm text-primary-text"
                      >
                        {{ getRelationSourceModelTitle(rel, m.title) }}
                      </td>
                      <td
                        class="!bg-transparent border-t border-secondary-border"
                      >
                        <nz-tag
                          class="!m-0 text-xs bg-alt-bold-background border-secondary-border text-primary-text"
                          >{{ rel.type }}</nz-tag
                        >
                      </td>
                      <td
                        class="!bg-transparent border-t border-secondary-border text-sm font-bold text-primary"
                      >
                        {{ getRelationTargetModelTitle(rel) }}
                      </td>
                      <td
                        class="!bg-transparent border-t border-secondary-border text-sm text-secondary-text"
                      >
                        @if (rel.keyFrom || rel.keyTo) {
                          <div class="flex flex-col gap-1">
                            @if (rel.keyFrom) {
                              <span class="text-xs"
                                >From:
                                <span
                                  class="font-mono text-primary-text bg-alt-background px-1 rounded"
                                  >{{ rel.keyFrom?.name || rel.keyFrom }}</span
                                ></span
                              >
                            }
                            @if (rel.keyTo) {
                              <span class="text-xs"
                                >To:
                                <span
                                  class="font-mono text-primary-text bg-alt-background px-1 rounded"
                                  >{{ rel.keyTo?.name || rel.keyTo }}</span
                                ></span
                              >
                            }
                          </div>
                        } @else {
                          <span class="text-xs italic text-placeholder">-</span>
                        }
                      </td>
                      <td
                        class="!bg-transparent border-t border-secondary-border text-sm text-secondary-text"
                      >
                        {{ rel.description }}
                        @if (rel.throughModel) {
                          <br /><span class="text-xs italic"
                            >Through:
                            <span class="font-bold text-primary">{{
                              rel.throughModel?.title ||
                                rel.throughModel?.name ||
                                rel.throughModel
                            }}</span></span
                          >
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </nz-table>
            </div>
          </section>
        }

        @if (m.endpoints && m.endpoints.length > 0) {
          <section
            class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden flex flex-col"
          >
            <div class="p-6 flex flex-col gap-4">
              <h2 class="text-2xl font-bold text-heading">
                <i class="fad fa-bolt mr-2 text-primary"></i
                >{{ m.title }}&nbsp;{{ config.texts.endpointsTitle }}
              </h2>
              <rapider-model-endpoints-table
                [endpoints]="m.endpoints"
                [baseModelName]="m.dataTableName || m.name"
              ></rapider-model-endpoints-table>
            </div>
          </section>
        }

        @if (hasValidAiConfig()) {
          @if (extractedAiConfig(); as aiCfg) {
            <section
              class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden flex flex-col"
            >
              <div class="p-6 flex flex-col gap-6">
                <h2 class="text-2xl font-bold text-heading">
                  <i class="fas fa-robot text-primary mr-2"></i
                  >{{ m.title }}&nbsp;{{ config.texts.aiConfigTitle }}
                </h2>

                <div class="flex flex-col gap-6 mt-2">
                  @if (aiCfg.vectorSearchFields) {
                    <div
                      class="bg-alt-background border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-3"
                    >
                      <h5
                        class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3"
                      >
                        {{ config.texts.previewAiVectorSearch }}
                      </h5>
                      <div class="flex flex-wrap gap-2 mt-2">
                        @for (field of aiCfg.vectorSearchFields; track field) {
                          <nz-tag
                            class="!m-0 text-sm bg-component-inner border-secondary-border text-primary-text px-3 py-1"
                            >{{ field }}</nz-tag
                          >
                        }
                      </div>
                    </div>
                  }

                  @if (aiCfg.planning?.availableOperations) {
                    <div
                      class="bg-alt-background border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-3"
                    >
                      <h5
                        class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3"
                      >
                        {{ config.texts.previewAiPlanning }}
                      </h5>
                      <div class="flex flex-wrap gap-2 mt-2">
                        @for (
                          op of aiCfg.planning.availableOperations;
                          track op
                        ) {
                          <nz-tag
                            class="!m-0 text-sm bg-primary/10 border-primary/20 text-primary px-3 py-1"
                            >{{ op | uppercase }}</nz-tag
                          >
                        }
                      </div>
                    </div>
                  }

                  @if (aiCfg.entityItemCreate; as createCfg) {
                    <div
                      class="bg-alt-background border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-6"
                    >
                      <h5
                        class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3"
                      >
                        {{ config.texts.previewAiCreate }}
                      </h5>

                      @if (createCfg.requiredFields) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiRequiredFields }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (req of createCfg.requiredFields; track req) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ req }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (createCfg.immutableFields) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiImmutableFields }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (f of createCfg.immutableFields; track f) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ f }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (createCfg.requiredRelations) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiRequiredRelations }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (
                              rel of createCfg.requiredRelations;
                              track rel
                            ) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ rel }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (createCfg.autoGenerateFields) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiAutoGenerate }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (f of createCfg.autoGenerateFields; track f) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ f }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (createCfg.requireUserConfirmation !== undefined) {
                        <div class="flex flex-col gap-1">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiRequireUserConf }}</span
                          >
                          <span
                            class="text-base font-medium text-primary-text"
                            >{{
                              createCfg.requireUserConfirmation ? 'Yes' : 'No'
                            }}</span
                          >
                        </div>
                      }

                      @if (createCfg.customValidationPrompt) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiCustomValidation }}</span
                          >
                          <span
                            class="text-sm text-primary-text bg-component-inner p-3 rounded border border-secondary-border"
                            >{{ createCfg.customValidationPrompt }}</span
                          >
                        </div>
                      }

                      @if (createCfg.additionalPrompt) {
                        <div
                          class="flex flex-col gap-3 mt-2 pt-4 border-t border-secondary-border"
                        >
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiPrompt }}</span
                          >
                          <div
                            class="markdown-normalize bg-component-inner p-5 rounded-lg border border-secondary-border text-primary-text w-full"
                          >
                            <rapider-markdown-preview
                              [data]="createCfg.additionalPrompt"
                            ></rapider-markdown-preview>
                          </div>
                        </div>
                      }
                    </div>
                  }

                  @if (aiCfg.entityItemUpdate; as updateCfg) {
                    <div
                      class="bg-alt-background border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-6"
                    >
                      <h5
                        class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3"
                      >
                        {{ config.texts.previewAiUpdate }}
                      </h5>

                      @if (updateCfg.immutableFields) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiImmutableFields }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (f of updateCfg.immutableFields; track f) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ f }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (updateCfg.autoGenerateFields) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiAutoGenerate }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (f of updateCfg.autoGenerateFields; track f) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ f }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (updateCfg.requireUserConfirmation !== undefined) {
                        <div class="flex flex-col gap-1">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiRequireUserConf }}</span
                          >
                          <span
                            class="text-base font-medium text-primary-text"
                            >{{
                              updateCfg.requireUserConfirmation ? 'Yes' : 'No'
                            }}</span
                          >
                        </div>
                      }

                      @if (updateCfg.customValidationPrompt) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiCustomValidation }}</span
                          >
                          <span
                            class="text-sm text-primary-text bg-component-inner p-3 rounded border border-secondary-border"
                            >{{ updateCfg.customValidationPrompt }}</span
                          >
                        </div>
                      }

                      @if (updateCfg.restrictedStates) {
                        <div class="flex flex-col gap-2 mt-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiRestrictedStates }}</span
                          >
                          <div class="flex flex-col gap-3">
                            @for (
                              state of updateCfg.restrictedStates;
                              track state.description
                            ) {
                              <div
                                class="bg-component-inner border border-secondary-border p-4 rounded-lg flex flex-col gap-2"
                              >
                                <span
                                  class="text-sm font-medium text-primary-text"
                                  >{{ state.description }}</span
                                >
                                <span
                                  class="text-xs font-mono text-secondary-text bg-alt-bold-background p-2 rounded border border-primary-border"
                                  >{{ state.where | json }}</span
                                >
                              </div>
                            }
                          </div>
                        </div>
                      }

                      @if (updateCfg.additionalPrompt) {
                        <div
                          class="flex flex-col gap-3 mt-2 pt-4 border-t border-secondary-border"
                        >
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiPrompt }}</span
                          >
                          <div
                            class="markdown-normalize bg-component-inner p-5 rounded-lg border border-secondary-border text-primary-text w-full"
                          >
                            <rapider-markdown-preview
                              [data]="updateCfg.additionalPrompt"
                            ></rapider-markdown-preview>
                          </div>
                        </div>
                      }
                    </div>
                  }

                  @if (aiCfg.entityRead; as readCfg) {
                    <div
                      class="bg-alt-background border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-6"
                    >
                      <h5
                        class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3"
                      >
                        {{ config.texts.previewAiRead }}
                      </h5>

                      <div class="flex gap-10 flex-wrap">
                        @if (readCfg.defaultPageSize) {
                          <div class="flex flex-col gap-1">
                            <span
                              class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                              >{{ config.texts.previewAiPageSize }}</span
                            >
                            <span
                              class="text-base font-mono font-bold text-primary-text"
                              >{{ readCfg.defaultPageSize }}</span
                            >
                          </div>
                        }

                        @if (readCfg.defaultContextLimit) {
                          <div class="flex flex-col gap-1">
                            <span
                              class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                              >{{ config.texts.previewAiContextLimit }}</span
                            >
                            <span
                              class="text-base font-mono font-bold text-primary-text"
                              >{{ readCfg.defaultContextLimit }}</span
                            >
                          </div>
                        }
                      </div>

                      @if (readCfg.hiddenFields) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiHiddenFields }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (f of readCfg.hiddenFields; track f) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ f }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (readCfg.maskedFields) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiMaskedFields }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (f of readCfg.maskedFields; track f) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ f }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (readCfg.additionalPrompt) {
                        <div
                          class="flex flex-col gap-3 mt-2 pt-4 border-t border-secondary-border"
                        >
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiPrompt }}</span
                          >
                          <div
                            class="markdown-normalize bg-component-inner p-5 rounded-lg border border-secondary-border text-primary-text w-full"
                          >
                            <rapider-markdown-preview
                              [data]="readCfg.additionalPrompt"
                            ></rapider-markdown-preview>
                          </div>
                        </div>
                      }
                    </div>
                  }

                  @if (aiCfg.entityItemDelete; as deleteCfg) {
                    <div
                      class="bg-alt-background border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-6"
                    >
                      <h5
                        class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3"
                      >
                        {{ config.texts.previewAiDelete }}
                      </h5>

                      @if (deleteCfg.requireCaptchaConfirmation !== undefined) {
                        <div class="flex flex-col gap-1">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiCaptchaConf }}</span
                          >
                          <span
                            class="text-base font-medium text-primary-text"
                            >{{
                              deleteCfg.requireCaptchaConfirmation
                                ? 'Yes'
                                : 'No'
                            }}</span
                          >
                        </div>
                      }

                      @if (deleteCfg.cascadingRelations) {
                        <div class="flex flex-col gap-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiCascading }}</span
                          >
                          <div class="flex flex-wrap gap-2">
                            @for (
                              rel of deleteCfg.cascadingRelations;
                              track rel
                            ) {
                              <nz-tag
                                class="!m-0 text-sm py-0.5 px-3 bg-component-inner border-secondary-border text-primary-text"
                                >{{ rel }}</nz-tag
                              >
                            }
                          </div>
                        </div>
                      }

                      @if (deleteCfg.restrictedStates) {
                        <div class="flex flex-col gap-2 mt-2">
                          <span
                            class="text-sm font-bold text-secondary-text uppercase tracking-wider"
                            >{{ config.texts.previewAiRestrictedStates }}</span
                          >
                          <div class="flex flex-col gap-3">
                            @for (
                              state of deleteCfg.restrictedStates;
                              track state.description
                            ) {
                              <div
                                class="bg-component-inner border border-secondary-border p-4 rounded-lg flex flex-col gap-2"
                              >
                                <span
                                  class="text-sm font-medium text-primary-text"
                                  >{{ state.description }}</span
                                >
                                <span
                                  class="text-xs font-mono text-secondary-text bg-alt-bold-background p-2 rounded border border-primary-border"
                                  >{{ state.where | json }}</span
                                >
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            </section>
          }
        }

        @if (extractedTitleTemplate(); as titleTemplate) {
          <section
            class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden flex flex-col"
          >
            <div class="p-6 flex flex-col gap-3">
              <h2 class="text-2xl font-bold text-heading">
                <i class="fad fa-font mr-2 text-primary"></i
                >{{ m.title }}&nbsp;{{ config.texts.previewTitleTemplate }}
              </h2>
              <div
                class="font-mono text-sm text-primary-text bg-alt-background border border-secondary-border px-5 py-3 rounded-lg w-max shadow-sm mt-2"
              >
                {{ titleTemplate }}
              </div>
            </div>
          </section>
        }

        @if (graphData().nodes.length > 1) {
          <section
            class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden flex flex-col"
          >
            <div
              class="p-6 border-b border-secondary-border bg-component-header"
            >
              <h2 class="text-2xl font-bold text-heading">
                <i class="fad fa-project-diagram mr-2 text-primary"></i
                >{{ m.title }}&nbsp;{{ config.texts.previewLocalDiagramTitle }}
              </h2>
            </div>
            <rapider-data-models-diagram
              [heightPx]="450"
              [graphData]="graphData()"
            ></rapider-data-models-diagram>
          </section>
        }

        <!-- DANGER ZONE: CASCADE DELETE TABLE (Paid Tier Only) -->
        @if (!isFreeTier()) {
          <section
            class="bg-component-inner border border-red-200 dark:border-red-900/50 rounded-2xl shadow-sm overflow-hidden flex flex-col mt-4"
          >
            <div class="p-6 flex flex-col gap-6">
              <!-- Danger Zone Header -->
              <div class="flex items-start gap-3.5 border-b border-secondary-border/60 pb-4">
                <div
                  class="w-11 h-11 rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20 flex items-center justify-center text-xl shrink-0"
                >
                  <i class="fad fa-triangle-exclamation"></i>
                </div>
                <div class="flex flex-col">
                  <h2 class="text-xl font-bold text-red-600 dark:text-red-400 m-0">
                    Danger Zone
                  </h2>
                  <p class="text-xs text-secondary-text mt-0.5 mb-0">
                    Irreversible actions for database table: <strong class="text-heading">{{ m.title }}</strong> (<code class="font-mono text-primary font-bold text-xs">{{ m.dataTableName || m.name }}</code>)
                  </p>
                </div>
              </div>

              <!-- Critical Consequences Breakdown Banner -->
              <div class="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 rounded-xl p-4 text-xs text-red-950 dark:text-red-200 flex flex-col gap-3 shadow-sm">
                <div class="flex items-center gap-2 font-bold text-sm text-red-700 dark:text-red-400">
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>Permanent Cascade Deletion Consequences</span>
                </div>
                <p class="leading-relaxed text-red-900 dark:text-red-300 m-0">
                  Deleting this database table will permanently remove the following resources across your workspace:
                </p>
                <ul class="list-disc list-inside space-y-1.5 pl-1 text-red-800 dark:text-red-300">
                  <li><strong>Database Table &amp; Rows:</strong> All stored records, columns, and data collections will be immediately dropped.</li>
                  <li><strong>API Endpoints:</strong> All auto-generated CRUD routes and custom endpoints for this model will be permanently deleted.</li>
                  <li><strong>Relations &amp; Foreign Keys:</strong> Inter-model relations and lookup bindings referencing this table will be severed.</li>
                  <li><strong>Application Breakage:</strong> Applications or workflows depending on this model will become unusable until reconfigured.</li>
                </ul>
              </div>

              <!-- Name Verification & Delete Action -->
              <div class="flex flex-col gap-3 pt-2">
                <label class="text-xs font-semibold text-heading">
                  To approve deletion, please type
                  <span class="font-mono font-bold text-danger bg-red-100 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-2 py-0.5 rounded select-all">
                    "{{ expectedDeleteName() }}"
                  </span>
                  below to confirm:
                </label>

                <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div class="flex-1 max-w-md">
                    <input
                      nz-input
                      type="text"
                      class="w-full !rounded-xl text-sm !py-2.5 bg-input-background text-primary-text border-secondary-border focus:!border-danger focus:!ring-danger/20"
                      [placeholder]="'Type \'' + expectedDeleteName() + '\' to confirm'"
                      [ngModel]="deleteConfirmInput()"
                      (ngModelChange)="deleteConfirmInput.set($event)"
                      (keydown.enter)="isDeleteNameMatched() && !isDeletingModel() && deleteCurrentModel()"
                    />
                  </div>

                  <button
                    nz-button
                    nzDanger
                    nzType="primary"
                    (click)="deleteCurrentModel()"
                    [disabled]="!isDeleteNameMatched() || isDeletingModel()"
                    [nzLoading]="isDeletingModel()"
                    class="font-bold rounded-xl px-5 py-2.5 h-auto shadow-sm transition-all flex items-center justify-center gap-2 text-xs shrink-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i class="fas fa-trash-alt text-xs"></i>
                    <span>Delete Table &amp; All Endpoints</span>
                  </button>
                </div>
              </div>
            </div>
          </section>
        } @else {
          <!-- Free Tier Managed Schema Banner -->
          <section
            class="bg-component-inner border border-primary-border rounded-2xl shadow-sm overflow-hidden flex flex-col p-6 mt-4"
          >
            <div class="flex items-start gap-4">
              <div
                class="w-11 h-11 rounded-xl bg-blue-500/10 text-primary border border-primary/20 flex items-center justify-center text-xl shrink-0"
              >
                <i class="fas fa-shield-alt"></i>
              </div>
              <div class="flex flex-col gap-1.5">
                <div class="flex items-center gap-2">
                  <h3 class="text-base font-bold text-heading m-0">
                    Protected Multi-Tenant Data Model
                  </h3>
                  <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary border border-primary/20">
                    <i class="fas fa-box text-[10px]"></i> Managed by App Store
                  </span>
                </div>
                <p class="text-xs text-secondary-text leading-relaxed m-0 max-w-2xl">
                  This table is provisioned and governed by an application installed from the App Store. Free tier workspaces share managed schemas and cannot delete tables or alter field structures directly.
                </p>
                <p class="text-xs text-secondary-text leading-relaxed m-0">
                  To create custom models, define custom fields, configure inter-model relationships, or manage dedicated database collections, please <strong>upgrade to a Paid workspace</strong>.
                </p>
              </div>
            </div>
          </section>
        }
      }
    </div>
  }
</div>

<nz-modal
  [(nzVisible)]="isFieldModalVisible"
  [nzTitle]="
    editingFieldId() ? config.texts.editFieldTitle : config.texts.addFieldTitle
  "
  [nzFooter]="null"
  [nzMaskClosable]="false"
  (nzOnCancel)="closeFieldModal()"
  nzWidth="800px"
  [nzStyle]="{ top: '20px' }"
>
  <ng-container *nzModalContent>
    <div
      class="flex flex-col gap-4 p-1 max-h-[75vh] overflow-y-auto custom-scrollbar pr-2 @container"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 shrink-0">
        <div class="flex flex-col gap-1.5">
          <label
            class="text-sm font-semibold text-primary-text"
            i18n="@@database.fieldName"
            >Name <span class="text-danger">*</span></label
          >
          <input
            nz-input
            [(ngModel)]="fieldForm().name"
            (blur)="autoGenerateTitle()"
            (ngModelChange)="nameError.set(null)"
            class="bg-input-background border-secondary-border focus:ring-focus-ring font-mono text-sm text-primary-text"
            placeholder="e.g., firstName"
          />
          @if (nameError()) {
            <div class="flex flex-col items-start gap-2 mt-1">
              <span class="text-danger text-xs font-medium">{{
                nameError()
              }}</span>
              <button
                nz-button
                nzType="default"
                nzSize="small"
                (click)="fixFieldName()"
                class="text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 shrink-0 shadow-sm focus:outline-none"
              >
                <i class="fas fa-wrench mr-1"></i> {{ config.texts.fixNameBtn }}
              </button>
            </div>
          }
        </div>
        <div class="flex flex-col gap-1.5">
          <label
            class="text-sm font-semibold text-primary-text"
            i18n="@@database.fieldTitle"
            >Title</label
          >
          <input
            id="fieldTitle"
            nz-input
            [(ngModel)]="fieldForm().title"
            class="bg-input-background border-secondary-border focus:ring-focus-ring text-sm text-primary-text"
            placeholder="e.g., First Name"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label
            class="text-sm font-semibold text-primary-text"
            i18n="@@database.fieldType"
            >Type <span class="text-danger">*</span></label
          >
          <nz-select [(ngModel)]="fieldForm().type" class="w-full">
            @for (type of fieldTypes; track type) {
              <nz-option [nzValue]="type" [nzLabel]="type"></nz-option>
            }
          </nz-select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label
            class="text-sm font-semibold text-primary-text"
            i18n="@@database.fieldDefault"
            >Default Value</label
          >
          <input
            nz-input
            [(ngModel)]="fieldForm().default"
            class="bg-input-background border-secondary-border focus:ring-focus-ring text-sm text-primary-text"
            placeholder="Hardcoded static default value"
          />
        </div>

        <div class="flex flex-col gap-1.5 md:col-span-2">
          <label class="text-sm font-semibold text-primary-text">{{
            config.texts.fieldDefaultFnLabel
          }}</label>
          <nz-select
            [(ngModel)]="fieldForm().defaultFn"
            nzAllowClear
            nzPlaceHolder="None"
            class="w-full"
          >
            <nz-option
              nzValue="guid"
              nzLabel="guid (MAC address + Time)"
            ></nz-option>
            <nz-option
              nzValue="uuid"
              nzLabel="uuid (MAC address + Time)"
            ></nz-option>
            <nz-option
              nzValue="uuidv4"
              nzLabel="uuidv4 (Random Alg)"
            ></nz-option>
            <nz-option
              nzValue="now"
              nzLabel="now (Current Date/Time)"
            ></nz-option>
          </nz-select>
          <span class="text-[10px] text-secondary-text">{{
            config.texts.fieldDefaultFnHint
          }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-1.5 shrink-0">
        <label
          class="text-sm font-semibold text-primary-text"
          i18n="@@database.fieldDesc"
          >Description</label
        >
        <textarea
          nz-input
          [(ngModel)]="fieldForm().description"
          rows="2"
          class="bg-input-background border-secondary-border focus:ring-focus-ring text-sm text-primary-text custom-scrollbar resize-y"
        ></textarea>
      </div>

      <div
        class="bg-alt-background border border-secondary-border rounded-lg p-4 grid grid-cols-2 @sm:grid-cols-3 gap-4 shrink-0"
      >
        <label class="flex items-center gap-2 cursor-pointer">
          <nz-switch
            [(ngModel)]="fieldForm().isRequired"
            nzSize="small"
          ></nz-switch>
          <span
            class="text-sm font-medium text-primary-text"
            i18n="@@database.required"
            >Required</span
          >
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <nz-switch
            [(ngModel)]="fieldForm().isUnique"
            nzSize="small"
          ></nz-switch>
          <span
            class="text-sm font-medium text-primary-text"
            i18n="@@database.unique"
            >Unique</span
          >
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <nz-switch
            [(ngModel)]="fieldForm().isArray"
            nzSize="small"
          ></nz-switch>
          <span
            class="text-sm font-medium text-primary-text"
            i18n="@@database.array"
            >Is Array</span
          >
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <nz-switch
            [(ngModel)]="fieldForm().isPrimaryKey"
            nzSize="small"
          ></nz-switch>
          <span
            class="text-sm font-medium text-primary-text"
            i18n="@@database.primaryKey"
            >Primary Key</span
          >
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <nz-switch
            [(ngModel)]="fieldForm().isHidden"
            nzSize="small"
          ></nz-switch>
          <span
            class="text-sm font-medium text-primary-text"
            i18n="@@database.hidden"
            >Hidden</span
          >
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <nz-switch
            [(ngModel)]="fieldForm().isSensitive"
            nzSize="small"
          ></nz-switch>
          <span
            class="text-sm font-medium text-primary-text"
            i18n="@@database.sensitive"
            >Sensitive</span
          >
        </label>
      </div>

      <div
        class="border rounded-lg p-4 flex flex-col gap-4 transition-colors shrink-0"
        [ngClass]="
          fieldForm().hasEnum
            ? 'bg-primary/5 border-primary/30 shadow-sm'
            : 'bg-component-inner border-primary-border'
        "
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <i class="fas fa-list-ol text-primary"></i>
            <span class="text-sm font-bold text-primary-text">{{
              config.texts.fieldEnumLabel
            }}</span>
          </div>
          <nz-switch [(ngModel)]="fieldForm().hasEnum"></nz-switch>
        </div>

        @if (fieldForm().hasEnum) {
          <div
            class="flex flex-col gap-3 animate-fade-in pt-3 border-t border-primary/20"
          >
            <div class="flex items-center gap-2 w-full">
              <input
                nz-input
                [(ngModel)]="enumInputValue"
                (keydown.enter)="addEnumValue()"
                [placeholder]="config.texts.fieldEnumHint"
                class="bg-input-background border-secondary-border focus:ring-focus-ring text-sm text-primary-text flex-1"
              />
              <button
                nz-button
                nzType="primary"
                (click)="addEnumValue()"
                class="bg-primary text-white border-none shadow-sm shrink-0 font-semibold focus:outline-none"
              >
                <i class="fas fa-plus mr-1"></i> {{ config.texts.addEnumBtn }}
              </button>
            </div>

            @if (fieldForm().enumValues && fieldForm().enumValues.length > 0) {
              <div
                class="flex flex-col gap-2 mt-2 bg-component-inner border border-secondary-border rounded-lg p-2"
                cdkDropList
                (cdkDropListDropped)="onEnumDrop($event)"
              >
                @for (
                  enumItem of fieldForm().enumValues;
                  track enumItem;
                  let i = $index
                ) {
                  <div
                    class="flex items-center justify-between p-2 border border-secondary-border rounded bg-body-background hover:border-primary transition-colors group"
                    cdkDrag
                  >
                    <div class="flex items-center gap-3 w-full">
                      <div
                        cdkDragHandle
                        class="cursor-grab active:cursor-grabbing text-secondary-text hover:text-primary transition-colors p-1 shrink-0"
                      >
                        <i class="fas fa-grip-vertical"></i>
                      </div>

                      @if (editingEnumIndex() === i) {
                        <input
                          nz-input
                          [(ngModel)]="editingEnumValue"
                          (keydown.enter)="saveEditEnum()"
                          class="h-8 text-sm flex-1 bg-input-background border-primary focus:ring-primary"
                          autofocus
                        />
                        <div class="flex items-center gap-1 shrink-0">
                          <button
                            nz-button
                            nzType="text"
                            nzSize="small"
                            (click)="saveEditEnum()"
                            class="text-success hover:bg-success/10"
                          >
                            <i class="fas fa-check"></i>
                          </button>
                          <button
                            nz-button
                            nzType="text"
                            nzSize="small"
                            (click)="cancelEditEnum()"
                            class="text-secondary-text hover:text-danger hover:bg-danger/10"
                          >
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                      } @else {
                        <span
                          class="text-sm font-medium text-primary-text flex-1 font-mono truncate"
                          >{{ enumItem }}</span
                        >
                        <div
                          class="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <button
                            nz-button
                            nzType="text"
                            nzSize="small"
                            (click)="startEditEnum(i, enumItem)"
                            class="text-secondary-text hover:text-primary"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <button
                            nz-button
                            nzType="text"
                            nzSize="small"
                            (click)="removeEnumValue(i)"
                            class="text-secondary-text hover:text-danger"
                          >
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div
                class="text-xs italic text-secondary-text bg-component-inner border border-dashed border-secondary-border rounded p-3 text-center"
              >
                No enum values added yet.
              </div>
            }
          </div>
        }
      </div>

      <div
        class="border rounded-lg p-4 flex flex-col gap-4 transition-colors shrink-0"
        [ngClass]="
          fieldForm().isCalculated
            ? 'bg-primary/5 border-primary/30 shadow-sm'
            : 'bg-component-inner border-primary-border'
        "
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <i class="fas fa-calculator text-primary"></i>
            <span
              class="text-sm font-bold text-primary-text"
              i18n="@@database.calculatedField"
              >Calculated Field</span
            >
          </div>
          <nz-switch [(ngModel)]="fieldForm().isCalculated"></nz-switch>
        </div>

        @if (fieldForm().isCalculated) {
          <div
            class="flex flex-col gap-3 animate-fade-in pt-3 border-t border-primary/20"
          >
            <div class="flex flex-col gap-1.5">
              <label
                class="text-xs font-bold text-secondary-text uppercase tracking-wider"
                i18n="@@database.calcType"
                >Calculation Type</label
              >
              <nz-select
                [ngModel]="'jsonata'"
                nzDisabled
                class="w-full @sm:w-1/2"
              >
                <nz-option nzValue="jsonata" nzLabel="JSONata"></nz-option>
              </nz-select>
            </div>
            <div class="flex flex-col gap-1.5">
              <label
                class="text-xs font-bold text-secondary-text uppercase tracking-wider"
                i18n="@@database.calcFormula"
                >Formula (JSONata Expression)
                <span class="text-danger">*</span></label
              >
              <textarea
                nz-input
                [(ngModel)]="fieldForm().calculationFormula"
                rows="3"
                placeholder="e.g. firstName & ' ' & lastName"
                class="bg-input-background border-secondary-border focus:ring-focus-ring font-mono text-sm text-primary-text custom-scrollbar resize-y"
              ></textarea>
              <span
                class="text-[10px] text-secondary-text"
                i18n="@@database.calcFormulaHint"
                >Evaluates dynamically on the server during create/update
                operations.</span
              >
            </div>
          </div>
        }
      </div>

      <div
        class="border border-secondary-border rounded-lg bg-component-inner shadow-sm overflow-hidden shrink-0 mt-2"
      >
        <div
          class="px-4 py-3 border-b border-secondary-border flex justify-between items-center cursor-pointer hover:bg-hover-default transition-colors"
          (click)="isExplanationCollapsed.set(!isExplanationCollapsed())"
        >
          <span
            class="text-sm font-bold text-primary-text flex items-center gap-2"
            ><i class="fas fa-info-circle text-primary"></i
            >{{ config.texts.fieldExplanationTitle }}</span
          >
          <i
            class="fas text-secondary-text"
            [ngClass]="
              isExplanationCollapsed() ? 'fa-chevron-down' : 'fa-chevron-up'
            "
          ></i>
        </div>
        @if (!isExplanationCollapsed()) {
          <div
            class="p-5 bg-alt-background text-sm text-primary-text border-t border-secondary-border grid grid-cols-1 @md:grid-cols-2 gap-4"
          >
            <div>
              <strong class="text-primary">Name:</strong> Unique database column
              name (no spaces).
            </div>
            <div>
              <strong class="text-primary">Title:</strong> Human-readable label.
            </div>
            <div>
              <strong class="text-primary">Type:</strong> Data type (string,
              number, boolean, etc).
            </div>
            <div>
              <strong class="text-primary">Default Function:</strong> DB-level
              function to generate value.
            </div>
            <div>
              <strong class="text-primary">Enum Values:</strong> Predefined
              allowed values for this field.
            </div>
            <div>
              <strong class="text-primary">Required:</strong> Value must be
              provided.
            </div>
            <div>
              <strong class="text-primary">Unique:</strong> Value must be
              distinct across records.
            </div>
            <div>
              <strong class="text-primary">Is Array:</strong> Can hold multiple
              values.
            </div>
            <div>
              <strong class="text-primary">Primary Key:</strong> Unique
              identifier row.
            </div>
            <div>
              <strong class="text-primary">Hidden:</strong> Excluded from API
              responses.
            </div>
            <div>
              <strong class="text-primary">Sensitive:</strong> Masked/encrypted.
            </div>
            <div>
              <strong class="text-primary">Calculated:</strong> Derived
              dynamically using JSONata.
            </div>
          </div>
        }
      </div>

      <div
        class="flex justify-end gap-3 mt-2 pt-4 border-t border-secondary-border shrink-0"
      >
        <button
          nz-button
          nzType="default"
          (click)="closeFieldModal()"
          class="border-secondary-border text-secondary-text font-semibold focus:outline-none hover:text-primary hover:border-primary"
          i18n="@@database.cancel"
        >
          Cancel
        </button>
        <button
          nz-button
          nzType="primary"
          (click)="saveField()"
          [nzLoading]="isFieldSaving()"
          [disabled]="
            !fieldForm().name ||
            !fieldForm().type ||
            (fieldForm().isCalculated && !fieldForm().calculationFormula)
          "
          class="bg-primary hover:bg-hover-primary text-primary-button-text font-bold border-none shadow-sm flex items-center gap-2 focus:outline-none disabled:opacity-50"
        >
          @if (!isFieldSaving()) {
            <i class="fas fa-save"></i>
          }
          <span i18n="@@database.saveField">Save Field</span>
        </button>
      </div>
    </div>
  </ng-container>
</nz-modal>
